import React, { useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import { Modal, Menu, Dropdown, Tooltip } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import uuid from 'uuid';
import Papa from 'papaparse';

import { Container, TitleWrapper, Button, IconWithText, Text, IconWrapper, BoxWrapper } from '../design';

import icPlus from 'utils/assetFiles/svgs/icPlus';
import Download from 'utils/assetFiles/svgs/Download';
import ArrowUp from 'utils/assetFiles/svgs/ArrowUp';
import icMenu from 'utils/assetFiles/svgs/icMenu';
import icPen from 'utils/assetFiles/svgs/icPen';
import icDelete from 'utils/assetFiles/svgs/icDelete';
import icEdit from 'utils/assetFiles/svgs/icEdit';

import AddStudent from './AddStudent';
import UploadCSV from './UploadCSV';
import { DownloadFileFromURL } from 'utils/helper';
import adminRequests from 'services/admin';
import EditStudent from './EditStudent';
import { generatePassword } from '../helper';
import { PageContext } from '../PageProvider';
import { message } from 'shell/components';

const HeaderButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 30px;
`;

const ListWrapper = styled.div`
    margin-bottom: 20px;
`;

const ListHeader = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr) 60px 1fr;
    border-bottom: 1px solid #eeeeee;
    padding-bottom: 10px;
`;

const ContentRowWrapper = styled.div`
    max-height: 300px;
    overflow: auto;
`;

const ContentRows = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr) 60px 1fr;
    border-bottom: 1px solid #eeeeee;
    padding: 10px 0px;
    :hover {
        background-color: #efedff;
        border-radius: 6px;
    }
    ${(props) =>
        props.invalid &&
        css`
            color: #db5019;
            background-color: #fce7e7;
        `}
`;

const OptionWrapper = styled.div`
    float: right;
    padding: 0px 5px;
    border-radius: 3px;
    :hover {
        background: #c0c0c0;
    }
`;

const HeaderItem = styled(Text)`
    color: #757575;
`;

const Cell = styled(Text)``;

const CellEnd = styled.div`
    justify-self: end;
    padding: 0px 8px;
`;

const SubmitBtnWrapper = styled.div`
    overflow: auto;
`;

const columns = ['studentId', 'name', 'group'];

const RegisterStudents = ({ complete, onEdit, onSubmit }) => {
    const { reviewData, setReviewData } = useContext(PageContext);

    const [addData, setAddData] = useState({});
    const [editData, setEditData] = useState({});
    const [addModal, setAddModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [uploadModal, setUploadModal] = useState(false);

    const downloadTemplate = () => {
        const blob = new Blob([columns.toString()], {
            type: 'text/plain;charset=utf-8',
        });
        const downloadUrl = URL.createObjectURL(blob);
        DownloadFileFromURL(downloadUrl, 'accounts.csv');
    };

    const downloadCSV = () => {
        const csvText = Papa.unparse(
            reviewData.students.map((s) => ({
                studentId: s.studentId,
                name: s.name,
                group: s.group,
                username: s.username,
                password: s.password,
                record: s.record,
            })),
            { header: true }
        );

        const blob = new Blob([csvText], { type: 'text/plain;charset=utf-8' });
        const downloadUrl = URL.createObjectURL(blob);
        DownloadFileFromURL(downloadUrl, 'accounts.csv');
    };

    const shouldDisableButton = () => {
        const invalidRow = reviewData.students.find((item) => item.invalid);
        return invalidRow ? true : false;
    };

    const handleRequest = async () => {
        setUploadModal(false);
    };

    const handleSubmitAdd = async (data) => {
        const { students = [], instructors = [] } = reviewData;
        const newUsers = [].concat(instructors, students);
        const strUsername = String(data.studentId).toLowerCase();

        if (newUsers.find((item) => item.username === strUsername)) {
            message.error('Username already exists in the list');
            return;
        }

        const user = await adminRequests.findUser({ usernmae: strUsername });
        const newStudent = [];

        if (user) {
            newStudent.push({
                id: user.id,
                studentId: user.studentId,
                username: user.username,
                name: user.name,
                group: data.group,
                record: true,
            });
        } else {
            newStudent.push({
                id: uuid.v4(),
                studentId: strUsername,
                username: strUsername,
                name: data.name,
                group: data.group,
                password: generatePassword(),
            });
        }

        const newStudents = [].concat(newStudent, students);

        setReviewData((prev) => ({ ...prev, students: newStudents }));
        setAddModal(false);
        setAddData({});
    };

    const handleSubmitEdit = async (data) => {
        const { students = [], instructors = [] } = reviewData;
        const newUsers = [].concat(instructors, students);
        const strUsername = String(data.studentId).toLowerCase();
        const duplUsername = newUsers.find((item) => item.username === strUsername && item.id !== data.id);

        if (duplUsername) {
            message.error('Username already exists in the list');
            return;
        }

        const user = await adminRequests.findUser({ username: strUsername });
        const newStudents = students.map((item) => {
            if (item.id === data.id) {
                return user
                    ? {
                          id: data.id,
                          studentId: user.username,
                          name: user.name,
                          group: data.group,
                          username: user.username,
                          record: true,
                      }
                    : {
                          id: data.id,
                          studentId: strUsername,
                          name: data.name,
                          group: data.group,
                          username: strUsername,
                          password: data.password,
                      };
            }
            return item;
        });

        setReviewData((prev) => ({ ...prev, students: newStudents }));
        setEditModal(false);
        setEditData({});
    };

    const handleClickDelete = (data) => {
        const { students } = reviewData;
        setReviewData((prev) => ({
            ...prev,
            students: students.filter((item) => {
                return item.id !== data.id;
            }),
        }));
    };

    const handleClickEdit = () => {
        if (onEdit) onEdit();
    };

    const handleSubmit = () => {
        if (onSubmit) onSubmit();
    };

    const handleCloseAdd = () => {
        setAddModal(false);
    };

    const handleCloseEdit = () => {
        setEditModal(false);
    };

    const handleOpenCSV = () => {
        setUploadModal(true);
    };

    const handleCancelUpload = () => {
        setUploadModal(false);
    };

    const handleOpenEdit = (data) => {
        setEditData(data);
        setEditModal(true);
    };

    const handleOpenAdd = () => {
        setAddData({ id: uuid.v4() });
        setAddModal(true);
    };

    const handleClickReset = () => {
        setReviewData((prev) => ({ ...prev, students: [] }));
    };

    return (
        <>
            <Container complete={complete}>
                <TitleWrapper complete={complete}>
                    Invite Students
                    {complete !== false && <IconWrapper onClick={handleClickEdit}>{icEdit}</IconWrapper>}
                </TitleWrapper>
                <BoxWrapper complete={complete}>
                    <HeaderButtons>
                        <IconWithText icon={Download} text='Download CSV Template' onClick={downloadTemplate} />
                        <IconWithText icon={Download} text='Download CSV' onClick={downloadCSV} />
                        <IconWithText icon={ArrowUp} text='Upload CSV' onClick={handleOpenCSV} />
                        <IconWithText id={'au_addStudent'} icon={icPlus} text='Add person' onClick={handleOpenAdd} />
                    </HeaderButtons>
                    <ListWrapper>
                        <ListHeader>
                            <HeaderItem>Student ID</HeaderItem>
                            <HeaderItem>Name</HeaderItem>
                            <HeaderItem>Group</HeaderItem>
                            <HeaderItem>Username</HeaderItem>
                            <HeaderItem>Password</HeaderItem>
                            <HeaderItem>Record</HeaderItem>
                        </ListHeader>
                        <ContentRowWrapper>
                            {reviewData.students.map((s) => {
                                return (
                                    <ContentRows key={s.username} invalid={s.invalid}>
                                        <Cell>{s.studentId}</Cell>
                                        <Cell>{s.name}</Cell>
                                        <Cell>{s.group}</Cell>
                                        <Cell>{s.username}</Cell>
                                        <Cell>{s.password}</Cell>
                                        <Cell>
                                            {s.record && (
                                                <Tooltip title='This user already has an Ensightful account'>
                                                    <WarningOutlined
                                                        style={{
                                                            color: 'orange',
                                                            fontSize: 16,
                                                            verticalAlign: 'middle',
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Cell>
                                        <CellEnd>
                                            <Dropdown
                                                overlay={
                                                    <Menu onClick={({ domEvent }) => domEvent.stopPropagation()}>
                                                        {!s.record && (
                                                            <Menu.Item onClick={() => handleOpenEdit(s)}>
                                                                <span style={{ paddingRight: 10 }}>{icPen}</span>
                                                                <span>Edit</span>
                                                            </Menu.Item>
                                                        )}
                                                        <Menu.Item onClick={() => handleClickDelete(s)}>
                                                            <span style={{ paddingRight: 10 }}>{icDelete}</span>
                                                            <span>Delete</span>
                                                        </Menu.Item>
                                                    </Menu>
                                                }
                                                placement='bottomRight'
                                                trigger={['click']}
                                            >
                                                <OptionWrapper onClick={(e) => e.stopPropagation()}>
                                                    {icMenu}
                                                </OptionWrapper>
                                            </Dropdown>
                                        </CellEnd>
                                    </ContentRows>
                                );
                            })}
                        </ContentRowWrapper>
                    </ListWrapper>
                    <SubmitBtnWrapper>
                        <Button
                            id={'au_studentNext'}
                            style={{ float: 'right' }}
                            onClick={handleSubmit}
                            disabled={shouldDisableButton()}
                        >
                            Next
                        </Button>
                        <Button style={{ float: 'right', marginRight: '10px' }} onClick={handleClickReset}>
                            Reset
                        </Button>
                    </SubmitBtnWrapper>
                </BoxWrapper>
            </Container>

            <Modal key={Math.random()} centered visible={addModal} onCancel={handleCloseAdd} footer={null}>
                <AddStudent data={addData} onAdd={handleSubmitAdd} onCancel={handleCloseAdd} />
            </Modal>
            <Modal key={Math.random()} centered visible={editModal} onCancel={handleCloseEdit} footer={null}>
                <EditStudent data={editData} onSave={handleSubmitEdit} onCancel={handleCloseEdit} />
            </Modal>
            <Modal
                closable={false}
                key={Math.random()}
                centered
                visible={uploadModal}
                onCancel={handleCancelUpload}
                footer={null}
            >
                <UploadCSV onRequest={handleRequest} />
            </Modal>
        </>
    );
};

export default RegisterStudents;
